import type { Map } from "ol";
import type TileLayer from "ol/layer/Tile";
import { transform } from "ol/proj";
import type { TileWMS } from "ol/source";

import { upsertOverlayInState } from "../addOverlays/overlayState";
import { getFeatureURLParamsObject } from "../getObjectsOnMapPoint/getObjectsOnMapPoint";

const ROAD_CAMERA_OVERLAY_ID = "road-camera-info";

type DisplayField = {
    name: string;
    label: string;
};

type ConfiguredLayerClickInteraction = {
    type: "overlay";
    overlayType: "roadCameraInfo";
    displayFields: DisplayField[];
};

type RoadCameraField = {
    label: string;
    value: string;
};

let latestConfiguredOverlayRequestId = 0;
let configuredOverlayRequestsInFlight = 0;

function setMapLoadingCursor(map: Map, isLoading: boolean) {
    const targetElement = map.getTargetElement?.();
    if (!targetElement) return;

    targetElement.style.cursor = isLoading ? "progress" : "";
}

function beginConfiguredOverlayRequest(map: Map) {
    configuredOverlayRequestsInFlight += 1;
    setMapLoadingCursor(map, true);
}

function endConfiguredOverlayRequest(map: Map) {
    configuredOverlayRequestsInFlight = Math.max(0, configuredOverlayRequestsInFlight - 1);
    if (configuredOverlayRequestsInFlight === 0) {
        setMapLoadingCursor(map, false);
    }
}

function waitForNextPaint() {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}

function getDisplayFields(interaction: ConfiguredLayerClickInteraction) {
    return Array.isArray(interaction?.displayFields)
        ? interaction.displayFields.filter(field => field?.name && field?.label)
        : [];
}

function buildRoadCameraFields(properties: any, displayFields: DisplayField[]): RoadCameraField[] {
    return displayFields
        .map((field) => {
            const value = properties?.[field.name];
            if (value === undefined || value === null || value === "") return null;
            return {
                label: field.label,
                value: String(value),
            };
        })
        .filter((field): field is RoadCameraField => Boolean(field));
}

function isLonLatCoordinate(coordinate: any[]) {
    return Math.abs(coordinate[0]) <= 180 && Math.abs(coordinate[1]) <= 90;
}

function getLonLatFromMapCoordinate(coordinate: any[], projection: any) {
    const [longitude, latitude] = transform(coordinate, projection, 'EPSG:4326');
    return { longitude, latitude };
}

function getOverlayPosition(feature: any, clickCoordinate: any[], projection: any) {
    const geometry = feature?.geometry;
    const coordinates = geometry?.type === "Point" ? geometry?.coordinates : null;

    if (Array.isArray(coordinates) && coordinates.length >= 2) {
        const pointCoordinate = [Number(coordinates[0]), Number(coordinates[1])];
        if (pointCoordinate.every(Number.isFinite)) {
            if (isLonLatCoordinate(pointCoordinate)) {
                const [longitude, latitude] = pointCoordinate;
                return { longitude, latitude };
            }
            return getLonLatFromMapCoordinate(pointCoordinate, projection);
        }
    }

    return getLonLatFromMapCoordinate(clickCoordinate, projection);
}

async function showConfiguredLayerOverlay(event: any, layer: TileLayer<TileWMS>, map: Map) {
    const interaction = layer?.get?.('clickInteraction') as ConfiguredLayerClickInteraction | undefined;
    if (interaction?.type !== "overlay" || interaction?.overlayType !== "roadCameraInfo") return false;

    const displayFields = getDisplayFields(interaction);
    if (displayFields.length === 0) return false;

    const source = layer?.getSource?.();
    const { VERSION, LAYERS, CQL_FILTER, STYLES } = source?.getParams?.() ?? {};
    const resolution = map.getView()?.getResolution();
    const projection = map.getView()?.getProjection();
    if (!source || typeof resolution !== "number" || !projection) return false;

    const featureInfoUrl = source?.getFeatureInfoUrl(
        event.coordinate,
        resolution,
        projection,
        getFeatureURLParamsObject({
            VERSION,
            LAYERS,
            CQL_FILTER,
            styles: STYLES,
            featureCount: 1,
        })
    );

    if (!featureInfoUrl) return false;

    const requestId = ++latestConfiguredOverlayRequestId;
    beginConfiguredOverlayRequest(map);

    try {
        const response = await fetch(featureInfoUrl);
        const data = await response.json();
        if (requestId !== latestConfiguredOverlayRequestId) return true;

        const feature = data?.features?.[0];
        if (!feature) return false;

        const fields = buildRoadCameraFields(feature?.properties, displayFields);
        if (fields.length === 0) return false;

        const position = getOverlayPosition(feature, event.coordinate, projection);
        upsertOverlayInState(
            {
                id: ROAD_CAMERA_OVERLAY_ID,
                type: "roadCameraInfo",
                position,
                data: { fields },
            }
        );
        await waitForNextPaint();
        return true;
    } catch (error) {
        console.error('Configured layer GetFeatureInfo error:', error);
        return false;
    } finally {
        endConfiguredOverlayRequest(map);
    }
}

export function activateConfiguredLayerClickInteractions(layers: any[] = [], map?: Map) {
    const interactiveLayers = layers
        .filter(layer => layer?.get?.('clickInteraction'))
        .reverse();

    if (!map || interactiveLayers.length === 0) {
        return {
            off: () => {
                // nothing
            },
        };
    }

    const clickFunc = async (event: any) => {
        for (const layer of interactiveLayers) {
            if (layer?.getVisible?.() === false) continue;
            const handled = await showConfiguredLayerOverlay(event, layer, map);
            if (handled) break;
        }
    };

    map.on("singleclick", clickFunc);

    return {
        off: () => {
            map.un("singleclick", clickFunc);
        }
    };
}
